import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getImageMapData } from '../../../../store/global/selectors';
import { resetImageMapData, setImageMapData } from '../../../../store/global/actions';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import Alert from '@material-ui/lab/Alert';
import axios from 'axios';
import {Raphael} from 'react-raphael'

const useStyles = makeStyles(() => ({
  imageAreaSelctorContainer: {
    maxWidth: '90vw',
    height: 'auto',
    position: 'relative',
    '& svg': {
      zIndex: 10
    },
    '& circle': {
      pointerEvents: 'none'
    },
    '& path': {
      cursor: 'pointer'
    },
    '& text':{ 
      pointerEvents: 'none'
    },
    '& rect':{
      pointerEvents: 'none'
    }
  },
}));

function ImageAreaSelector({ imageURL, setOpenAssetsExplorer, drawerBodyRef }) {

  //------------------------------- Hooks -------------------------------------------
  const classes = useStyles();
  const canvasRef = useRef(null);
  const paperRef = useRef(null);
  const imageRef = useRef(null);
  const dispatch = useDispatch();

  //------------------------------- Redux Selectors ----------------------------------
  const { selectedTool, areas, shapeToEdit } = useSelector(getImageMapData);

  //------------------------------- States ------------------------------------------
  const [currentShape, setCurrentShape] = useState(null);
  const [paper, setPaper] = useState(null);
  const [isDrawing, setDrawing] = useState(false);
  const [selections, setSelections] = useState([])
  const [shapeElements, setShapeElements] = useState([]);
  const [isPathClosed, setPathClosed] = useState(false);

  const [lastFilledIndex, setLastFilledIndex] = useState(-1);
  const [imageSize, setImageSize] = useState({ w: 0, h: 0 });
  const [imageMissing, setImageMissing] = useState(false);


  //------------------------------- Functions ----------------------------------------
  const getMeta = (url, cb) => {
    const img = new Image();
    img.onload = () => cb(img.width, img.height, img);
    img.onerror = (err) => cb(err);
    img.src = url;
  };

  const onClickImportBtn = () => {
    setOpenAssetsExplorer(true);
  };


  /**
   * @param {Event} event 
   * This function will call on clicking on the paper.If the tool is selected and not aleady a shape then this function will exectue
   */
  const handleStartDrawing = (event) => {
    if(!isDrawing && selectedTool && event.target.tagName.toLowerCase() !== 'path'){
      setDrawing(true)

      console.log(isPathClosed);

      if(isPathClosed){
        setPathClosed(false)
        setDrawing(false)
        return true
      }

      const {x, y} = getPositionInPaper(event);    
  
      // This set is used to group the dots joining lines, shape and the text
      const set = paper.set();

      const mark = markPoint(x, y)
      set.push(mark)

      let currentSelection = [x,y]
      
      const polygon = paper.path(`M ${x} ${y}`)
      polygon.attr({fill: 'rgba(15, 10, 222,0.3)', stroke: 'rgba(15, 10, 222,1)'})
      set.push(polygon)

      /**
       * @param {*} event 
       * Callback function for pass into event listener.
       * This function keeps a reference so we can remove the event listener. If we are passing a anonymous function into the event listeners then we can't remove the event listeners
       */
      const handleMouseMove = (event) => handleMouseMoveForDrawing(event, polygon, currentSelection)
      
      /**
       * @param {*} event 
       * Callback function for pass into event listener.
       * This function keeps a reference so we can remove the event listener. If we are passing a anonymous function into the event listeners then we can't remove the event listeners
       */
      const handleMouseDown = (event) => {
        const {isEnd, coordinate} = handleMouseDownForDrawing(event, polygon, currentSelection, set)
        currentSelection = [...currentSelection, ...coordinate]

        if(isEnd){
          paperRef.current.removeEventListener('mousemove',handleMouseMove)
          paperRef.current.removeEventListener('mousedown',handleMouseDown)
          setDrawing(false);
          
          const selectionDetails = {
            shape: 'poly',
            coords: convertCoordsIntoPercentage(currentSelection),
            name: generateAreaName()
          }

          set.push(...appendLabelAndClickEvent(currentSelection, selectionDetails.name, polygon))

          setShapeElements([...shapeElements, {name: selectionDetails.name, shape: set}])
          setSelections([...selections, selectionDetails])
          dispatch(setImageMapData({areas: [...selections, selectionDetails]}))

          setPathClosed(true)
        }
      }
  
      paperRef.current.addEventListener('mousemove', handleMouseMove)
      paperRef.current.addEventListener('mousedown', handleMouseDown)
    }
  }
  
  /**
   * @param {list of coordinates of the currently selecting area} currentSelection 
   * @param {Which label text do we need to place on the top of area} label 
   * @param {reference of the shape. This is used to attach click event into the shape} shape 
   * @returns an array contains the elements text and textBg
   */
  const appendLabelAndClickEvent = (currentSelection, label, shape) => {
    const [cx, cy] = findCenterOfPolygon(currentSelection)
    const text = paper.text(cx,cy, label)
    const textBox = text.getBBox();
    const textBg = paper.rect(textBox.x - (textBox.width / 2), textBox.y - 5, (textBox.width * 2), textBox.height + 10).attr({fill: 'rgba(255, 255, 255,0.5)', stroke: 'rgba(15, 10, 222,0.0)'})
    text.attr({fill: '#000', 'font-size': 12})
    text.toFront();
    shape.click(() => handleClickOnShapes(label))
    paper.setFinish();

    return [text, textBg]
  }

  /**
   * @param {Name of the shape} label
   * This function is used to open the area property drawer by clicking on the elements(Areas)
   */
  const handleClickOnShapes = (label) => {
    dispatch(setImageMapData({selectedAreaName: label, shapeToEdit: label}))
  }

  /**
   * 
   * @param {Event object of the mouse move} event 
   * @param {Reference of the current polygon drawing} polygon 
   * @param {Array of the coordinates of the current selection} currentSelection 
   * 
   * function for for draw the line from the previously plotted point to the current mouse pointer on moving mouse pointer
   */
  const handleMouseMoveForDrawing = (event, polygon, currentSelection) => {
    const {x,y} = getPositionInPaper(event);

    let path = `M${currentSelection[0]} ${currentSelection[1]}`

    for(let i=2;i<currentSelection.length;i=i+2){
      path += ` L${currentSelection[i]} ${currentSelection[i+1]}`
    }

    path += ` L${x} ${y}`

    polygon.attr({path})
  }

  /**
   * 
   * @param {Event object of the mouse move} event 
   * @param {Reference of the current polygon drawing} polygon 
   * @param {Array of the coordinates of the current selection} currentSelection 
   * @param {Raphael set in which the shape is belongs to} set
   * @returns An object which contains isEnd:It will be true if mouse down on the starting position of the shape
   *          coordinates: An array in which first element is x coordinate of and the second element is y coordinate of the point we marked
   * This function will draw a line from the last point the current mouse down point and then mark a point there. This function is also handling the shape closing
   */
  const handleMouseDownForDrawing = (event, polygon, currentSelection, set) => {
    let {x,y} = getPositionInPaper(event);

    const firstX = currentSelection[0];
    const firstY = currentSelection[1];

    const isInEnd = isInEndRange({x:firstX, y:firstY}, {x,y})

    if(isInEnd){
      x=firstX;
      y=firstY;
    }

    let path = `M${currentSelection[0]} ${currentSelection[1]}`

    for(let i=2;i<currentSelection.length;i=i+2){
      path += ` L${currentSelection[i]} ${currentSelection[i+1]}`
    }

    path += ` L${x} ${y}`

    polygon.attr({path})
    
    if(!isInEnd){
      const mark = markPoint(x, y)
      set.push(mark)
    }

    return {
      coordinate: [x,y],
      isEnd: isInEnd
    }
  }

  /**
   * @param {Event object} event 
   * @returns an object with property x and y. Here, the x and y are the coordinate of the current mouse pointer inside the element that passes the event
   */
  const getPositionInPaper = (event) => {
    const rect = event.target.getBoundingClientRect();

    const { clientX, clientY } = event;
    const {left, top} = rect

    let x = clientX - left;
    let y = clientY - top;

    return {x, y};
  }

  // Function for checking whether a coordinate is inside the RADIUS range of the first coordinate
  // Returns true if the currentCoordinate is in tha RADIUS range of the firstCoordinate else it will return false
  const isInEndRange = (firstCoordinate, currentCoordinate) => {
    const RADIUS = 10;

    const { x, y } = firstCoordinate;
    const { x: currentX, y: currentY } = currentCoordinate;

    // * distance between two coordinates =√((x2 – x1)² + (y2 – y1)²)
    if (Math.sqrt(Math.pow(Math.abs(currentX - x), 2) + Math.pow(Math.abs(currentY - y), 2)) <= RADIUS) {
      return true;
    }

    return false;
  };

  // Function for marking the points
  const markPoint = (x, y) => {
    const circle = paper.circle(x, y, 5)
    circle.attr({fill: 'rgba(15, 10, 222,0.7)', stroke: 'rgba(15, 10, 222,1)'})
    circle.toFront()
    return circle
  };

  /**
   * Function for generating by default area names.
   * By default the area names will be in this pattern "Area_1", "Area_2"....
   */
  const generateAreaName = () => {
    let regex = /Area_(\d+)/;

    // Filter and extract numbers following 'Area_' in the name
    let numbers = areas?.reduce((acc, item) => {
      let match = item.name.match(regex);
      if (match) {
        acc.push(parseInt(match[1]));
      }
      return acc;
    }, []);
    const highestNumber = Math.max(...(numbers || []));
    const numberOfAreas = areas?.length;

    if (highestNumber > numberOfAreas) {
      return `Area_${highestNumber + 1}`;
    }
    return `Area_${numberOfAreas + 1}`;
  }

  /**
   * @param {coordinates of the polygon} coords 
   * @returns an array in which first and second elements are the x and y coordinate of the center point respectively
   * This function will help us to find the center point of a polygon
   */
  const findCenterOfPolygon = (coords) => {
    var x = coords.map ((coord, index) => index % 2 === 0 ? coord : null ).filter((item) => item !== null);
    var y = coords.map ((coord, index) => index % 2 !== 0 ? coord : null ).filter((item) => item !== null);
    console.log({x,y});
    var cx = (Math.min (...x) + Math.max (...x)) / 2;
    var cy = (Math.min (...y) + Math.max (...y)) / 2;
    return [cx, cy];
  }

  /**
   * @param {Array of coordinates of a shape} coords 
   * @returns coords array converted to respected percentage value
   * This function will help us to convert the actual value of the coords array into percentage
   */
  const convertCoordsIntoPercentage = (coords) => {
    const convertedCoords = coords.map((coordinate,index) => (coordinate / (index % 2 === 0 ? imageSize.w : imageSize.h)) * 100)
    return convertedCoords;
  }

  /**
   * @param {Array of coordinates of a shape with actual value} coords 
   * @returns coords array converted to respected actual value from percentage value
   * This function will help us to convert the percentage value of the coords array into actual value
   */
  const convertCoordsIntoActualValue = (coords) => {
    if (typeof coords === "string") coords = coords.split(",");
    const convertedCoords = coords.map((coordinate, index) => (coordinate * (index % 2 === 0 ? imageSize.w : imageSize.h)) / 100)
    return convertedCoords;
  }

  /**
   * @param {Data for draw a shape. It will be a object which contains name, coords and type of the shape} data
   * This function is used to draw polygons and group all the elements in the shape into a set and then store this set into the shapeElements state
   */
  const drawPolygon = (data) => {
    let {coords, name } = data;
    coords = convertCoordsIntoActualValue(coords);
    paper.setStart()
    const set = paper.set()

    let path = `M${coords[0]} ${coords[1]}`;
    
    for(let i=2;i<coords.length;i=i+2){
      path += ` L${coords[i]} ${coords[i+1]}`
    }

    const polygon = paper.path(`M${coords[0]} ${coords[1]}`)
    polygon.attr({path, fill: 'rgba(15, 10, 222,0.3)', stroke: 'rgba(15, 10, 222,1)'})
    polygon.data("data-ui-tour-id", "drawen-area")

    set.push(polygon)

    for(let i=0; i<coords.length; i=i+2){
      const circle = markPoint(coords[i],coords[i+1])
      set.push(circle)
    }

    set.push(...appendLabelAndClickEvent(coords, name, polygon))

    setShapeElements(prev => [...prev, {name, shape: set}])
  }

  // This function is used to mark all selected areas on loading image map
  const markSelectedAreas = () => {
    areas?.forEach((item) => drawPolygon(item))
  }
  //------------------------------ Lifecycle Hooks -------------------------------------
  // This is used to store the width and height of the image into the state imageSize
  useEffect(() => {
    if (imageURL) {
      console.log({imageURL});
      getMeta(imageURL, (w, h) => {
        setImageSize({ w, h });
      });
    }
  }, [imageURL]);

  // This will help to make the pointerEvents of path elements in svg without pointer events. We can avoid inverse event bubbling
  useEffect(() => {
    if (paperRef.current) {
      const area = document.getElementById('image-map-editor-area')
      const paths = area.getElementsByTagName('path')
      for (let path of paths) {
          if (isDrawing) {
              path.style.pointerEvents = 'none';
          } else {
              path.style.pointerEvents = 'auto';
          }
      }
  }
  },[isDrawing])
  
  // This will help to handle updation of shapes on editing and deleting of the areas
  useEffect(() => {
    const selectionArrLength = selections?.length;
    const areasArrLength = areas?.length;

    if(shapeToEdit && selectionArrLength === areasArrLength){
      const previousShapesName = shapeElements.map((item) => item.name);
      const filteredArea = areas.find(item => !previousShapesName.includes(item.name))
      const elementToChange = shapeElements.find(item => item.name === shapeToEdit);

      console.log({previousShapesName, filteredArea, elementToChange, selections, areas});

      if(selections.length === areas.length && filteredArea){
        elementToChange?.shape.remove()
        setShapeElements(prev => prev.filter(item => item.name !== shapeToEdit))
        drawPolygon(filteredArea)
      }
    }else if(selectionArrLength - areasArrLength === 1){
      const shapeNames = areas.map((item) => item.name)
      const elementToChange = shapeElements.find(shape => !shapeNames.includes(shape.name))
      console.log({elementToChange, areas, shapeElements,  selections}, 'this is the element to change');
      elementToChange?.shape.remove();
      setShapeElements(prev => prev.filter(shape => shapeNames.includes(shape.name)))
    }

    dispatch(setImageMapData({shapeToEdit: null}))
    setSelections(areas)
  },[areas])

  // This is used to set the selection very initially
  useEffect(() => {
    if(selections?.length === 0){
      setSelections(areas)
    }
  },[areas])

  const handleImageExistenceError = () => {
    setImageMissing(true)
    dispatch(setImageMapData({ areas: null }))
  }

  // This will set the raphael paper very initially
  useEffect(() => {
    if(imageSize?.w !== 0 && imageSize?.h !== 0 && !paper && paperRef?.current){
      const raphaelPaper = Raphael(paperRef?.current, '100%', '100%')
      setPaper(raphaelPaper)
    }
  }, [areas, paperRef.current, imageSize]);

  // This will call the function markSelectedArea on initial loading. So if there any shapes to it will draw
  useEffect(() => {
    if(paper){
      markSelectedAreas();
    }
  },[paper])

  return (
    <Box className={classes.imageAreaSelctorContainer} style={{ cursor: selectedTool ? 'crosshair' : '' }} id={"selectedImageContainer"}>
      {(imageURL === 'DELETED' || imageMissing) ? (
        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          <Alert severity='warning'>Could not load the image from asset</Alert>
          <Button variant="contained" onClick={onClickImportBtn} className={classes.importBtn} startIcon={<SystemUpdateAltIcon style={{ marginRight: 6 }} />}>
            Import new image
          </Button>
        </Box>
      ) : (
        <>
          <div ref={paperRef} id='image-map-editor-area' style={{width: `${imageSize.w}px`, height: `${imageSize.h}px`, position: 'relative', zIndex: 2}} onClick={handleStartDrawing}>
            <img src={imageURL} onError={handleImageExistenceError} style={{ position: 'absolute', top: 0, left: 0, zIndex: 1, userSelect: 'none' }} ref={imageRef} />
          </div>
        </>
      )}
    </Box>
  );
}

export default ImageAreaSelector;